import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useUserStore from '@/store/user';
import { needsPermissionCheck, isAccountExpired } from '@/utils/auth';

// 路由渲染策略枚举
export enum RouteRenderType {
  ACCOUNT_EXPIRED = 'ACCOUNT_EXPIRED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  NORMAL = 'NORMAL',
}

/**
 * 路由权限检查 Hook
 * 负责检查当前路由的权限状态并返回相应的渲染类型
 */
export const useRoutePermission = () => {
  const location = useLocation();
  const { userInfo } = useUserStore();

  // 检查当前路由是否需要权限验证
  const routeNeedsPermissionCheck = useMemo(
    () => needsPermissionCheck(location.pathname),
    [location.pathname]
  );

  // 检查账号是否过期
  const accountExpired = useMemo(
    () => userInfo && isAccountExpired(userInfo),
    [userInfo]
  );

  // 检查是否有权限访问（未授权且需要权限验证的路由）
  const hasNoPermission = useMemo(
    () => userInfo && !userInfo.is_auth && routeNeedsPermissionCheck,
    [userInfo, routeNeedsPermissionCheck]
  );

  // 获取当前路由应该渲染的类型
  const getRouteRenderType = useMemo((): RouteRenderType => {
    if (accountExpired && routeNeedsPermissionCheck) {
      return RouteRenderType.ACCOUNT_EXPIRED;
    }
    if (hasNoPermission) {
      return RouteRenderType.UNAUTHORIZED;
    }
    return RouteRenderType.NORMAL;
  }, [accountExpired, routeNeedsPermissionCheck, hasNoPermission]);

  return {
    routeNeedsPermissionCheck,
    accountExpired,
    hasNoPermission,
    getRouteRenderType,
  };
};
